import { useQueryClient } from '@tanstack/react-query';
import klydoApiClient from '../../../api/klydo';
import useKlydoMutation, {
  UseKlydoMutationOptions,
} from '../../../hooks/useKlydoMutation';

export type UseRequestPublicationMutationParams = {
  klydoId: string;
  /** Desired go-live date once approved. Always sent — see the api client. */
  publicationTimestamp: number;
  comment?: string;
};

/**
 * Opens the artist's PUBLICATION REQUEST for a klydo.
 *
 * The request carries no klydo content — the api reads the stored klydo and
 * validates that, so callers MUST have persisted the current content (an awaited
 * `upsertDraftKlydo`) before calling this, or the review runs against stale data.
 */
const useRequestPublicationMutation = (
  options?: UseKlydoMutationOptions<
    void,
    UseRequestPublicationMutationParams,
    void
  >,
) => {
  const queryClient = useQueryClient();

  return useKlydoMutation({
    // `options` is spread FIRST and the hook's own handlers wrap the caller's, so a
    // caller passing `onSettled` cannot silently drop the cache invalidation.
    //
    // Success is NOT announced here. The editor opens `PublishSuccessModal`
    // instead, which is the only place that can also say when the klydo goes live —
    // and the only feedback the Collection branch, which runs no mutation at all,
    // could ever have.
    ...options,
    mutationKey: ['request-publication'],
    mutationFn: ({ klydoId, publicationTimestamp, comment }) => {
      return klydoApiClient.requestPublication(
        klydoId,
        publicationTimestamp,
        comment,
      );
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['klydo', variables.klydoId],
      });
      queryClient.invalidateQueries({ queryKey: ['klydosList'] });
      options?.onSettled?.(data, error, variables);
    },
  });
};

export default useRequestPublicationMutation;
